/**
 * Download Settings Modal Component
 * Allows users to customize PDF redaction settings before downloading.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Download, FileText, AlertCircle } from 'lucide-react';
import { usePostHog } from '@posthog/react';
import { generateCustomRedactedPdf } from '../../services/cloudFunctions';
import { auth } from '../../services/firebase';

const STORAGE_KEY = 'suredox_download_settings';

const DEFAULT_SETTINGS = {
  redactionMode: 'redact',
  colorPreset: 'black',
  customColor: '#000000',
  includeLog: false
};

export default function DownloadSettingsModal({
  isOpen,
  onClose,
  documentId,
  documentName,
  activeMethod
}) {
  const posthog = usePostHog();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setSettings({ ...DEFAULT_SETTINGS, ...parsed });
      }
    } catch (e) {
      console.error('Failed to load download settings:', e);
    }
  }, []);

  // Save settings to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      console.error('Failed to save download settings:', e);
    }
  }, [settings]);

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setError(null);
  };

  const getRedactionColor = () => {
    if (settings.colorPreset === 'custom') {
      return settings.customColor;
    }
    return settings.colorPreset === 'white' ? '#FFFFFF' : '#000000';
  };

  const handleDownload = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      const result = await generateCustomRedactedPdf(documentId, {
        source: 'redacted',
        redactionMode: settings.redactionMode,
        redactionColor: getRedactionColor(),
        includeLog: settings.includeLog,
        activeMethod: activeMethod
      });

      if (!result.success) {
        setError(result.error || 'Failed to generate PDF');
        return;
      }

      const uid = auth.currentUser?.uid;
      if (uid && posthog) {
        posthog.capture('redacted_pdf_downloaded', {
          document_id: documentId,
          document_name: documentName,
          source: 'redacted',
          redaction_mode: settings.redactionMode,
          include_log: settings.includeLog,
          active_method: activeMethod,
        });
      }

      // Download PDF to Downloads folder
      if (result.pdfUrl) {
        try {
          const response = await fetch(result.pdfUrl);
          if (!response.ok) throw new Error('Download failed');

          const blob = await response.blob();
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = result.fileName || `${documentName}_redacted.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);
        } catch (downloadError) {
          console.error('PDF download failed:', downloadError);
          // Fallback to opening in new tab
          window.open(result.pdfUrl, '_blank');
        }
      }

      // Download log if requested
      if (result.logUrl) {
        // Small delay to avoid popup blocker
        setTimeout(async () => {
          try {
            const response = await fetch(result.logUrl);
            if (!response.ok) throw new Error('Log download failed');

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = result.logFileName || `${documentName}_redaction_log.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
          } catch (err) {
            console.error('Log download failed:', err);
            // Fallback to opening in new tab
            window.open(result.logUrl, '_blank');
          }
        }, 500);
      }

      onClose();
    } catch (err) {
      console.error('Download failed:', err);
      posthog?.captureException(err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isGenerating && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Download Settings</DialogTitle>
          <DialogDescription>
            Configure download settings for {documentName || 'this document'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Document Info */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
            <FileText className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {documentName || 'Document'}
              </p>
            </div>
          </div>

          {/* Redaction Mode */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm text-gray-700 dark:text-white">Redaction Mode</h3>
            <div className="space-y-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="redactionMode"
                  value="redact"
                  checked={settings.redactionMode === 'redact'}
                  onChange={() => updateSetting('redactionMode', 'redact')}
                  className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Redact (hide content)
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Completely hide sensitive information with solid color
                  </p>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="redactionMode"
                  value="highlight"
                  checked={settings.redactionMode === 'highlight'}
                  onChange={() => updateSetting('redactionMode', 'highlight')}
                  className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Highlight (show content with overlay)
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Show content with colored highlight overlay
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Redaction Color - only show if redact mode */}
          {settings.redactionMode === 'redact' && (
            <div className="space-y-3">
              <h3 className="font-semibold text-sm text-gray-700 dark:text-white">Redaction Color</h3>
              <div className="space-y-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="colorPreset"
                    value="black"
                    checked={settings.colorPreset === 'black'}
                    onChange={() => updateSetting('colorPreset', 'black')}
                    className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  <div className="w-6 h-6 bg-black rounded border border-gray-300 dark:border-gray-600" />
                  <span className="text-sm text-gray-900 dark:text-white">Black</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="colorPreset"
                    value="white"
                    checked={settings.colorPreset === 'white'}
                    onChange={() => updateSetting('colorPreset', 'white')}
                    className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  <div className="w-6 h-6 bg-white rounded border border-gray-300 dark:border-gray-600" />
                  <span className="text-sm text-gray-900 dark:text-white">White</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="colorPreset"
                    value="custom"
                    checked={settings.colorPreset === 'custom'}
                    onChange={() => updateSetting('colorPreset', 'custom')}
                    className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  <input
                    type="color"
                    value={settings.customColor}
                    onChange={(e) => {
                      setSettings(prev => ({ ...prev, customColor: e.target.value, colorPreset: 'custom' }));
                      setError(null);
                    }}
                    className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                  />
                  <span className="text-sm text-gray-900 dark:text-white">Custom Color</span>
                </label>
              </div>
            </div>
          )}

          {/* Redaction Log */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm text-gray-700 dark:text-white">Download Redaction Log</h3>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.includeLog}
                onChange={(e) => updateSetting('includeLog', e.target.checked)}
                className="mt-0.5 w-4 h-4 text-emerald-600 focus:ring-emerald-500 rounded"
              />
              <div>
                <span className="text-sm text-gray-900 dark:text-white">
                  Include a CSV file with redaction details
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Lists all redacted items with page numbers and categories
                </p>
              </div>
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isGenerating}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDownload}
            disabled={isGenerating}
            className="w-full sm:w-auto"
          >
            {isGenerating ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Generating...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Download
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
